import React, { useState, useEffect } from 'react';
import { getMSPTenants } from '../utils/mspApi';
import FinOpsDashboard from '../../component/user/FineOps';
import { Spinner } from 'react-bootstrap';
import toastr from 'toastr';

const API_BASE = 'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod';

const MSPFinOpsV2 = ({ mspConfig, isSidebarOpen }) => {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mspSummary, setMspSummary] = useState(null);
  const [tenantCosts, setTenantCosts] = useState({});

  const loadMSPData = React.useCallback(async () => {
    try {
      const tenantsData = await getMSPTenants(mspConfig.mspID);
      setTenants(tenantsData.tenants || []);
      
      // Load cost data for each tenant
      const costs = {};
      for (const tenant of tenantsData.tenants || []) {
        try {
          const response = await fetch(`${API_BASE}/v2/finops/dashboard?tenantID=${tenant.tenantID}`);
          if (response.ok) {
            const data = await response.json();
            costs[tenant.tenantID] = data.cost?.total || 0;
          }
        } catch (error) {
          console.error(`Error loading cost for tenant ${tenant.tenantID}:`, error);
          costs[tenant.tenantID] = 0;
        }
      }
      setTenantCosts(costs);
      
      // Calculate MSP summary
      const totalCost = Object.values(costs).reduce((sum, cost) => sum + cost, 0);
      setMspSummary({
        totalCost,
        tenantCount: tenantsData.tenants?.length || 0,
        totalResources: 0 // Could calculate from tenant data if needed
      });
      
    } catch (error) {
      console.error('Error loading MSP data:', error);
      toastr.error('Failed to load MSP data');
    } finally {
      setLoading(false);
    }
  }, [mspConfig.mspID]);

  useEffect(() => {
    loadMSPData();
  }, [loadMSPData]);

  const getTenantDisplayName = (tenant) => {
    return tenant.companyName || tenant.email.split('@')[0];
  };

  const handleTenantSelect = (tenant) => {
    // Set tenantID in localStorage temporarily for the tenant view
    localStorage.setItem('msp_selected_tenant', tenant.tenantID);
    localStorage.setItem('msp_original_tenant', localStorage.getItem('tenantID') || '');
    localStorage.setItem('tenantID', tenant.tenantID);
    setSelectedTenant(tenant);
  };

  const handleBackToMSP = () => {
    // Restore original tenantID
    const originalTenant = localStorage.getItem('msp_original_tenant');
    if (originalTenant) {
      localStorage.setItem('tenantID', originalTenant);
    } else {
      localStorage.removeItem('tenantID');
    }
    localStorage.removeItem('msp_selected_tenant');
    localStorage.removeItem('msp_original_tenant');
    setSelectedTenant(null);
  };

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 120px)', paddingTop: '80px' }}>
        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
        <p className="mt-3" style={{ color: '#342ead', fontSize: '1.1rem' }}>Loading MSP FinOps...</p>
      </div>
    );
  }

  if (tenants.length === 0) {
    return (
      <div className="alert alert-info m-4">
        <h4>No Managed Tenants</h4>
        <p>You don't have any managed tenants yet. Add tenants to view their FinOps data.</p>
      </div>
    );
  }

  // If tenant is selected, show the regular tenant FinOps view
  if (selectedTenant) {
    return (
      <div>
        {/* Back to MSP button */}
        <div className="container-fluid p-2 bg-light border-bottom">
          <button 
            className="btn btn-outline-secondary btn-sm"
            onClick={handleBackToMSP}
          >
            ← Back to MSP Dashboard
          </button>
          <span className="ms-3 text-muted">
            Viewing FinOps for: <strong>{getTenantDisplayName(selectedTenant)}</strong>
          </span>
        </div>
        
        {/* Regular tenant FinOps dashboard */}
        <div>
          <style>{`
            .cloudmap-page { padding-top: 0 !important; margin-top: 0 !important; }
            .cloudmap-page .container { padding-top: 0 !important; }
          `}</style>
          <FinOpsDashboard isSidebarOpen={isSidebarOpen} />
        </div>
      </div>
    );
  }

  // MSP overview with tenant cards
  return (
    <div className="container-fluid p-4">
      {/* MSP Summary */}
      <div className="row mb-4">
        <div className="col-12">
          <h2 className="mb-3">MSP FinOps Dashboard</h2>
          <p className="text-muted">Select a tenant to view their detailed FinOps dashboard</p>
        </div>
      </div>
      
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted">Total AWS Spend</h6>
              <h3 className="text-primary">${mspSummary?.totalCost?.toFixed(2) || '0.00'}</h3>
              <small className="text-muted">Across {mspSummary?.tenantCount || 0} tenants</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted">Managed Tenants</h6>
              <h3 className="text-secondary">{tenants.length}</h3>
              <small className="text-muted">Active customers</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted">Average Spend</h6>
              <h3 className="text-info">
                ${tenants.length > 0 ? ((mspSummary?.totalCost || 0) / tenants.length).toFixed(2) : '0.00'}
              </h3>
              <small className="text-muted">Per tenant</small>
            </div>
          </div>
        </div>
      </div>

      {/* Tenant Cards */}
      <div className="row">
        <div className="col-12">
          <h4 className="mb-3">Managed Tenants</h4>
        </div>
        {tenants.map(tenant => {
          const cost = tenantCosts[tenant.tenantID] || 0;
          return (
            <div key={tenant.tenantID} className="col-md-6 col-lg-4 mb-4">
              <div 
                className="card h-100 shadow-sm" 
                style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                onClick={() => handleTenantSelect(tenant)}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h5 className="card-title mb-0">{getTenantDisplayName(tenant)}</h5>
                    <span className="badge bg-success">Active</span>
                  </div>
                  
                  <p className="text-muted small mb-2">{tenant.email}</p>
                  
                  <div className="row text-center mt-3">
                    <div className="col-6">
                      <h4 className="text-primary mb-0">${cost.toFixed(2)}</h4>
                      <small className="text-muted">MTD Cost</small>
                    </div>
                    <div className="col-6">
                      <h4 className="text-info mb-0">
                        {(() => {
                          const cloudAccounts = tenant.cloudAccounts;
                          if (!cloudAccounts) return 0;
                          const accounts = typeof cloudAccounts === 'string' ? JSON.parse(cloudAccounts) : cloudAccounts;
                          const standaloneCount = (accounts.aws || []).length;
                          const orgCount = (accounts.organizations || []).reduce((sum, org) => {
                            return sum + 1 + (org.memberAccounts || []).filter(m => m.connected).length;
                          }, 0);
                          return standaloneCount + orgCount;
                        })()
                      }
                      </h4>
                      <small className="text-muted">Accounts</small>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <small className="text-muted">
                      Plan: {tenant.subscriptionPlan?.plan || 'Basic'} | 
                      Created: {new Date(tenant.createdAt).toLocaleDateString()}
                    </small>
                  </div>
                </div>
                
                <div className="card-footer bg-transparent">
                  <button className="btn btn-sm w-100" style={{ backgroundColor: '#342ead', color: 'white' }}>
                    View FinOps Dashboard →
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MSPFinOpsV2;
